import Link from 'next/link'
import React, { useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { SettingItem } from 'src/components/settings/SettingsList'
import IntegrationApps from 'src/components/settings/IntegrationApps'
import SearchBar from '../common/search-and-filters/subcomponents/SearchBar'
import { filterRewardsApps, findAndConfigureApps } from 'src/lib/utils/integrationsHelpers'
import { integrationsConfig, integrationsConfigType } from 'src/assets/SettingsConfig'
import { AppStorePromoCard } from '../common/misc/AppStorePromoCard'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'
import { useUser } from 'src/lib/contexts/UserContext'
import { permissionType } from 'src/lib/utils/constants'
import { useLocale } from 'src/lib/hooks/useLocal'

interface IntegrationsProps {
  apps: [any]
  shippingApps: any[]
  rewardsApps: any
}

const Integrations: React.FC<IntegrationsProps> = ({ apps, shippingApps, rewardsApps }) => {
  const router = useRouter()
  const { s_id } = router.query
  const [searchText, setSearchText] = useState('')
  const { canAccess } = useUser()
  const { t } = useLocale()

  const {
    getters: { organisationData }
  } = useOrganisationContext()

  const etdAppData = useMemo(() => {
    return organisationData?.shopfloApps?.find((app: any) => app.name === 'DYNAMIC_ETD')
  }, [organisationData?.shopfloApps])

  const matchedApps = findAndConfigureApps(apps, 'id', 'type', searchText)
  const adsAndAnalyticsApps = matchedApps?.filter((app: any) => app.category === 'analytics')
  const reMarketingApps = matchedApps?.filter((app: any) => app.category === 'marketing')

  const shippingIntegrationApps = findAndConfigureApps(shippingApps, 'etd_provider', 'type', searchText)
  const rewardsIntegrationApps = filterRewardsApps(integrationsConfig, rewardsApps, searchText)
  const etdIntegrationPermission = canAccess(permissionType.DYNAMIC_ETD_INTEGRATION, 0)
  const additionalScriptPermission = canAccess(permissionType.ADDITIONAL_SCRIPT, 0)

  return (
    <>
      {Boolean(s_id) ? (
        <>
          <IntegrationApps />
        </>
      ) : (
        <>
          <SearchBar
            placeholder='Search Integrations'
            searchText={searchText}
            onChange={setSearchText}
            handleSearchSubmit={() => {}}
            clearSearch={() => setSearchText('')}
            customCss='w-full bg-white'
            submitHandlerRequired={false}
          ></SearchBar>
          {matchedApps?.length > 0 || shippingIntegrationApps.length > 0 || rewardsIntegrationApps.length > 0 ? (
            <>
              <IntegrationItemsLayout label='ADS & ANALYTICS' apps={adsAndAnalyticsApps} />
              <IntegrationItemsLayout label='RE-MARKETING' apps={reMarketingApps} />
              <IntegrationItemsLayout
                disabled={!etdIntegrationPermission}
                label='SHIPPING'
                apps={shippingIntegrationApps}
              />
              {!etdIntegrationPermission && (
                <div className='mt-2 bg-white rounded-lg'>
                  <AppStorePromoCard
                    heading='Enable Dynamic Estimated Delivery Time display on checkout'
                    subheading='Powered by your shipping provider, integrate real time delivery date on your checkout.'
                    appId={etdAppData?.id}
                    appDetailPath={etdAppData?.id ? `/apps-integrations/detail?id=${etdAppData.id}` : '/apps-integrations'}
                  />
                </div>
              )}
              <IntegrationItemsLayout label='REWARDS' apps={rewardsIntegrationApps} />
              {additionalScriptPermission && (
                <IntegrationItemsLayout
                  label={t('thank_you_screen.title')}
                  apps={[
                    {
                      type: integrationsConfigType.ADDITIONAL_SCRIPTS,
                      label: 'Additional Scripts',
                      imgPath: '/images/additional-scripts.svg',
                      isIntegrationPage: false
                    }
                  ]}
                />
              )}
            </>
          ) : (
            <p className='text-center py-4 text-gray-500'>No integrations found</p>
          )}
        </>
      )}
    </>
  )
}

interface IntegrationItemsLayoutProps {
  label: string
  apps: any[]
  disabled?: boolean
}

const IntegrationItemsLayout: React.FC<IntegrationItemsLayoutProps> = ({ label, apps, disabled = false }) => {
  if (!Boolean(apps?.length)) return null
  return (
    <>
      <p className='px-5 py-3 text-[12px] text-gray-dark'>{label}</p>
      <div className={`relative ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <ul className='flex flex-col w-full border border-white rounded-lg overflow-hidden'>
          {apps.map((app: any) => {
            if (!Boolean(app)) return <></>

            return (
              <Link
                href={{
                  pathname: '/apps-integrations',
                  query: { s_id: app?.type }
                }}
                key={app?.type}
                passHref
              >
                <li key={app?.type}>
                  <SettingItem
                    type={app?.type ?? ''}
                    imgPath={app?.imgPath}
                    is_mandatory={false}
                    label={app?.label ?? 'NA'}
                    tagText={!Boolean(app) ? 'Not configured yet' : app?.tagText}
                    tagColor='black'
                    permissionId={app?.permissionId}
                    isIntegrationPage={app?.isIntegrationPage}
                    is_configured={app?.isConfigured}
                    enabled={app?.enabled}
                  />
                </li>
              </Link>
            )
          })}
        </ul>
      </div>
    </>
  )
}

export default Integrations
